/*----------------------------------
- AGENDA PAGE CONTENT CATALOG (SSOT)
----------------------------------*/

/**
 * @docs docs/features/agenda
 * @rule Copy states what the agenda holds (TLD launch phases, root zone changes, industry
 *       events) and what a member adds in Product, never a figure the page cannot read from
 *       the backend, never where any data comes from beyond the GeoNames credit the map owes.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { AGENDA_BOARD_DEFAULT_COPY, type AgendaBoardCopy } from '@uniquedomains/components/agenda/board/boardCopy';
import { NEWSLETTER_AGENDA_DAYS_AHEAD } from '@uniquedomains/components/contracts/newsletter/public';

import type { LandingSignupPersona } from '@/client/catalogs/landing/publicLinks';

/*----------------------------------
- NEWSLETTER BAND
----------------------------------*/

/**
 * The newsletter band of the agenda and of the three TLD pages (`TldNewsletterBand`). The form subscribes to the whole newsletter, so the body names
 * the other two weekly sends too: if the band ever subscribes to the agenda alone, its second
 * sentence is the one string that changes.
 */
export const AGENDA_NEWSLETTER_BAND_CONTENT = {
    heading: 'The agenda by email, every Wednesday',
    // "the weekly news", never "the news brief": "brief" is not a noun in the product's words (Gaetan, 2026-10-25; the
    // mock `website-agenda.html` reads it so).
    body: `TLD launch phases, registry changes and industry events for the next ${NEWSLETTER_AGENDA_DAYS_AHEAD} days. You also get the weekly news on Monday and one-word opportunities on Thursday. Free, one click to leave.`,
} as const;

/*----------------------------------
- CONTENT
----------------------------------*/

const AGENDA_LEDE = 'New TLD launch phases, root zone changes and domain industry events, by date.';

export const AGENDA_PAGE_CONTENT = {
    meta: {
        pageTitle: 'Domaining Agenda: TLD launch dates, registry changes and industry events',
        metaDescription:
            'New TLD launch phases, root zone changes and domain industry events, month by month on a calendar, a map and a list by day. Add it to your calendar app with a free feed.',
        /** The `text/calendar` alternate link's title: the feed a calendar app finds in the head. */
        calendarFeedTitle: 'Unique Domains agenda calendar feed',
        markdownTitle: 'Markdown version',
    },
    /**
     * The board's words: the shared defaults with the page's own title (the one `h1`) and lede, and the event pop-up's
     * words as the website mock writes them ("Location", "Close the event details"): the website opens industry events
     * only, so the close names the event where Product's names the date.
     */
    board: {
        ...AGENDA_BOARD_DEFAULT_COPY,
        title: 'Domaining Agenda',
        lede: AGENDA_LEDE,
        // The page is the agenda, Product's the calendar: the inert Previous names the page it is on.
        previousBound: (monthLabel: string) => `The agenda starts in ${monthLabel}`,
        detail: { ...AGENDA_BOARD_DEFAULT_COPY.detail, close: 'Close the event details', where: 'Location' },
    } satisfies AgendaBoardCopy,
    feeds: {
        /** The title bar's popover pill. */
        summary: 'Add to calendar',
        heading: 'Calendar feeds',
        // A click on an https feed downloads a one-time copy in most browsers: the copy names the
        // subscribe-by-link step, which is what keeps new dates arriving.
        body: 'Add a feed by its link in Google Calendar, Apple Calendar or Outlook. New dates appear on their own.',
        allLabel: 'Everything',
        copy: 'Copy link',
        copied: 'Copied',
        copyLabel: (feed: string) => `Copy the ${feed} feed link`,
    },
    myDates: {
        eyebrow: 'Your dates',
        heading: 'See your own dates next to these',
        body: 'Your calendar in Unique Domains adds your watchlist expiries and Radar dates to this agenda, with a private calendar feed of its own.',
        ctaLabel: 'Get my dates',
        /** The persona pair beside "Get my dates", as the mock and the shipped pair read it. */
        personaCtas: { investor: "I'm an Investor", founder: "I'm a Founder" } satisfies Record<LandingSignupPersona, string>,
    },
    relatedLinksTitle: 'Continue with Unique Domains',
    unavailable: 'The agenda is temporarily unavailable. Please try again shortly.',
} as const;
